import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  ContentType,
  DsButtonVariants,
  DsColors,
  Icons,
  SortDir,
  TableCellData,
  TableCellTextAlign,
  TableColumnConfig,
  TableCustomCellContext,
  TableEmptyStateConfig,
  TableRow,
  TableSortEvent,
} from '@levelaccess/design-system';

import { TranslateService } from '../../../../translate/translate.service';
import { ORGANIZATIONS_ADDONS_SEPARATOR, OrganizationsTableColumn } from './organizations-table.constants';
import { ITableEmptyState } from '../../../../components/table/ngb-table/utilities/ngb-table.interface';

@Component({
  selector: 'app-organizations-table',
  templateUrl: './organizations-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OrganizationsTableComponent {
  private readonly translateService: TranslateService = inject(TranslateService);

  public readonly rows: InputSignal<TableRow[]> = input<TableRow[]>([]);
  public readonly caption: InputSignal<string> = input<string>('');
  public readonly emptyState: InputSignal<ITableEmptyState | null> = input<ITableEmptyState | null>(null);
  public readonly sortColumnKey: InputSignal<string | null> = input<string | null>(null);
  public readonly sortDirection: InputSignal<SortDir> = input<SortDir>(null);
  public readonly paginationAriaLabel: InputSignal<string> = input<string>('');
  public readonly page: InputSignal<number> = input<number>(1);
  public readonly pageSize: InputSignal<number> = input<number>(10);
  public readonly pageSizeOptions: InputSignal<number[]> = input<number[]>([5, 10, 25, 50]);
  public readonly collectionSize: InputSignal<number> = input<number>(0);

  public readonly sortChange: OutputEmitterRef<TableSortEvent> = output<TableSortEvent>();
  public readonly pageChange: OutputEmitterRef<number> = output<number>();
  public readonly pageSizeChange: OutputEmitterRef<number> = output<number>();

  public readonly Icons: typeof Icons = Icons;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;

  private readonly addonsCellTemplate: Signal<TemplateRef<TableCustomCellContext>> =
    viewChild.required<TemplateRef<TableCustomCellContext>>('addonsCellTemplate');
  private readonly statusCellTemplate: Signal<TemplateRef<TableCustomCellContext>> =
    viewChild.required<TemplateRef<TableCustomCellContext>>('statusCellTemplate');

  public readonly dsEmptyState: Signal<TableEmptyStateConfig | null> = computed((): TableEmptyStateConfig | null => {
    const emptyState: ITableEmptyState | null = this.emptyState();
    if (!emptyState) {
      return null;
    }

    return {
      icon: emptyState.dsPageMessage?.icon ?? emptyState.dsIcon?.icon,
      iconColor: emptyState.dsPageMessage?.iconColor ?? (emptyState.dsIcon?.color as DsColors | undefined),
      iconStyle: emptyState.dsPageMessage?.iconStyle ?? emptyState.dsIcon?.style,
      iconScale: emptyState.dsPageMessage?.iconScale ?? emptyState.dsIcon?.scale,
      label: emptyState.title,
      body: emptyState.subtitle,
    };
  });

  public readonly columns: Signal<TableColumnConfig[]> = computed((): TableColumnConfig[] => [
    {
      key: OrganizationsTableColumn.NAME,
      header: this.translateService.instant('tenant_name'),
      contentType: ContentType.textLink,
      sortable: true,
      styles: { width: '25%' },
      textAlign: TableCellTextAlign.left,
    },
    {
      key: OrganizationsTableColumn.PACKAGE,
      header: this.translateService.instant('label_package'),
      contentType: ContentType.string,
      styles: { width: '14%' },
      textAlign: TableCellTextAlign.left,
    },
    {
      key: OrganizationsTableColumn.ADDONS,
      header: this.translateService.instant('label_addons'),
      contentType: ContentType.custom,
      cellTemplate: this.addonsCellTemplate(),
      styles: { width: '20%' },
      textAlign: TableCellTextAlign.left,
    },
    {
      key: OrganizationsTableColumn.SSO_CONFIG,
      header: this.translateService.instant('sso'),
      contentType: ContentType.custom,
      cellTemplate: this.statusCellTemplate(),
      styles: { width: '18%' },
      textAlign: TableCellTextAlign.left,
    },
    {
      key: OrganizationsTableColumn.ACADEMY_CONFIG,
      header: this.translateService.instant('academy'),
      contentType: ContentType.custom,
      cellTemplate: this.statusCellTemplate(),
      styles: { width: '18%' },
      textAlign: TableCellTextAlign.left,
    },
    {
      key: OrganizationsTableColumn.ACTIONS,
      header: this.translateService.instant('table_column_actions'),
      contentType: ContentType.dropdown,
      styles: { width: '5%' },
      textAlign: TableCellTextAlign.left,
    },
  ]);

  protected getAddonLines(cell: TableCellData | undefined): string[] {
    return typeof cell?.value === 'string' ? cell.value.split(ORGANIZATIONS_ADDONS_SEPARATOR) : [];
  }

  protected isConfigured(cell: TableCellData | undefined): boolean {
    return cell?.attributes?.['configured'] === true;
  }
}
